import { FileText, LayoutGrid, CalendarCheck, BookOpen, GraduationCap, HelpCircle, Download, Search } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { BOOKING_PORTAL_URL } from "@/lib/booking-portal";

/* ── Navigation types ─────────────────────────────────── */

export interface FeaturedContent {
  title: string;
  description: string;
  href: string;
  ctaLabel: string;
}

export interface NavChildRich {
  label: string;
  href: string;
  icon: LucideIcon;
  description: string;
}

export interface NavItemMega {
  label: string;
  type: "mega";
  children: NavChildRich[];
  featured: FeaturedContent;
}

export interface NavItemSimple {
  label: string;
  type: "simple";
  children: { label: string; href: string }[];
}

export interface NavItemLink {
  label: string;
  href: string;
}

export type NavItem = NavItemMega | NavItemSimple | NavItemLink;

export function isMegaNavItem(item: NavItem): item is NavItemMega {
  return "type" in item && item.type === "mega";
}

export function isSimpleDropdownNavItem(item: NavItem): item is NavItemSimple {
  return "type" in item && item.type === "simple";
}

export function isLinkNavItem(item: NavItem): item is NavItemLink {
  return "href" in item && !("type" in item);
}

/* ── Shared featured content ──────────────────────────── */

export const FEATURED_EBOOK: FeaturedContent = {
  title: "Nowe zasady szczepień psów 2026",
  description: "Bezpłatny ebook o zmianach w przepisach dot. szczepień przeciw wściekliźnie",
  href: "/ebooks/szczepienia-psow-wscieklizna-2026.pdf",
  ctaLabel: "Pobierz ebook PDF",
};

/* ── NAV_ITEMS ────────────────────────────────────────── */

export const NAV_ITEMS: { main: NavItem[]; cta: { label: string; href: string } } = {
  main: [
    {
      label: "Produkt",
      type: "mega",
      children: [
        {
          label: "Lista funkcjonalności",
          href: "/funkcjonalnosci",
          icon: LayoutGrid,
          description: "Poznaj wszystkie możliwości Vetiny",
        },
        {
          label: "Vetina Booking",
          href: "/booking",
          icon: CalendarCheck,
          description: "Rezerwacje online 24/7 dla Twoich klientów",
        },
        {
          label: "Znajdź weterynarza",
          href: BOOKING_PORTAL_URL,
          icon: Search,
          description: "Baza placówek i rezerwacja wizyt dla właścicieli",
        },
        {
          label: "Cennik",
          href: "/#cennik",
          icon: FileText,
          description: "Sprawdź ile kosztuje Vetina",
        },
      ],
      featured: FEATURED_EBOOK,
    },
    {
      label: "Porównaj",
      type: "simple",
      children: [
        { label: "Wszystkie porównania", href: "/porownaj" },
        { label: "vs. Klinika XP", href: "/porownaj/klinika-xp" },
        { label: "vs. Veterinaro", href: "/porownaj/veterinaro" },
      ],
    },
    {
      label: "Zasoby",
      type: "mega",
      children: [
        {
          label: "Blog",
          href: "/blog",
          icon: BookOpen,
          description: "Poradniki, porównania i aktualności",
        },
        {
          label: "Akademia Vetina",
          href: "/akademia",
          icon: GraduationCap,
          description: "Materiały szkoleniowe do systemu",
        },
        {
          label: "FAQ",
          href: "/faq",
          icon: HelpCircle,
          description: "Najczęściej zadawane pytania",
        },
        {
          label: "Broszura PDF",
          href: "/downloads/Vetina_Broszura_2026.pdf",
          icon: Download,
          description: "Przekonaj szefa - pobierz przegląd funkcji",
        },
      ],
      featured: FEATURED_EBOOK,
    },
  ],
  cta: { label: "Umów Demo", href: "/kontakt" },
};

export const HERO = {
  headline: "Godzina dziennie więcej dla pacjentów",
  subheadline:
    "Każdy dzień bez Vetiny to 60 minut straconych na dokumentację zamiast na pacjentów. Zaprojektowany od zera dla szybkości - mniej kliknięć, zero opóźnień, niezawodność gdy najbardziej potrzebujesz.",
  primaryCta: "Umów bezpłatne demo",
  secondaryCta: "Zobacz funkcje",
  stats: [
    { value: "2 min", label: "oszczędności na wizytę" },
    { value: "6:00–22:00", label: "dostępny support" },
    { value: "99.9%", label: "gwarancji dostępności" },
  ],
};

export const PRICING = {
  headline: "Vetina to nie koszt - to inwestycja w lepsze jutro. Z nami oszczędzasz średnio 2 minuty na każdej wizycie.",
  subheadline:
    "Dostosuj parametry do swojej placówki i zobacz konkretne liczby",
  cta: "Zapytaj o cenę dla Twojej placówki",
};

export const BROCHURE = {
  label: "Przekonaj swojego szefa",
  description:
    "Pobierz gotową broszurę z przeglądem funkcji i korzyści.",
  cta: "Pobierz broszurę PDF",
  href: "/downloads/Vetina_Broszura_2026.pdf",
};
